import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { AppLayout } from '@/components/AppLayout';
import { RequireAuth } from '@/features/auth/RequireAuth';

/**
 * Every screen is its own chunk. Nothing beyond the shell and the sign-in form
 * is downloaded until the route is actually visited — so an employee never
 * fetches the administration bundle.
 */
const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const ChangePasswordPage = lazy(() => import('@/features/auth/ChangePasswordPage'));
const HomePage = lazy(() => import('@/features/home/HomePage'));

const EmployeeListPage = lazy(() => import('@/features/employees/EmployeeListPage'));
const EmployeeDetailPage = lazy(() => import('@/features/employees/EmployeeDetailPage'));
const DirectoryPage = lazy(() => import('@/features/employees/DirectoryPage'));
const ProfilePage = lazy(() => import('@/features/employees/ProfilePage'));

const LeavePage = lazy(() => import('@/features/leave/LeavePage'));
const SicknessPage = lazy(() => import('@/features/sickness/SicknessPage'));
const DocumentsPage = lazy(() => import('@/features/documents/DocumentsPage'));
const HandbookPage = lazy(() => import('@/features/handbook/HandbookPage'));
const PoliciesPage = lazy(() => import('@/features/handbook/PoliciesPage'));
const ContractsPage = lazy(() => import('@/features/onboarding/ContractsPage'));
const BenefitsPage = lazy(() => import('@/features/benefits/BenefitsPage'));
const LearningPage = lazy(() => import('@/features/learning/LearningPage'));
const MessagesPage = lazy(() => import('@/features/messaging/MessagesPage'));

const ApprovalsPage = lazy(() => import('@/features/admin/ApprovalsPage'));
const UsersAdminPage = lazy(() => import('@/features/users/UsersAdminPage'));
const ActivatePage = lazy(() => import('@/features/auth/ActivatePage'));
const ResetPasswordPage = lazy(() => import('@/features/auth/ResetPasswordPage'));
const PayrollAdminPage = lazy(() => import('@/features/documents/PayrollAdminPage'));
const ControlledDocumentsPage = lazy(() => import('@/features/documents/ControlledDocumentsPage'));
const PolicyAdminPage = lazy(() => import('@/features/handbook/PolicyAdminPage'));
const ContractAdminPage = lazy(() => import('@/features/onboarding/ContractAdminPage'));
const NewStarterPage = lazy(() => import('@/features/onboarding/NewStarterPage'));
const BenefitAdminPage = lazy(() => import('@/features/benefits/BenefitAdminPage'));
const CourseAdminPage = lazy(() => import('@/features/learning/CourseAdminPage'));
const NotificationsPage = lazy(() => import('@/features/messaging/NotificationsPage'));
const EmployeeAdminPage = lazy(() => import('@/features/employees/EmployeeAdminPage'));
const OrganisationPage = lazy(() => import('@/features/organisation/OrganisationPage'));
const HelpPage = lazy(() => import('@/features/admin/HelpPage'));
const CalendarPage = lazy(() => import('@/features/analytics/CalendarPage'));
const AnalyticsPage = lazy(() => import('@/features/analytics/AnalyticsPage'));
const SettingsPage = lazy(() => import('@/features/admin/SettingsPage'));
const DataPage = lazy(() => import('@/features/admin/DataPage'));

export const routes: RouteObject[] = [
  {
    path: '/sign-in',
    element: <LoginPage />,
  },
  {
    // Outside RequireAuth by necessity: the caller has no session yet.
    path: '/activate/:token',
    element: <ActivatePage />,
  },
  {
    path: '/reset/:token',
    element: <ResetPasswordPage />,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <HomePage /> },
      { path: 'change-password', element: <ChangePasswordPage /> },

      // --- self service -----------------------------------------------------
      { path: 'profile', element: <ProfilePage /> },
      { path: 'leave', element: <LeavePage /> },
      { path: 'sickness', element: <SicknessPage /> },
      { path: 'documents', element: <DocumentsPage /> },
      { path: 'contract', element: <ContractsPage /> },
      { path: 'benefits', element: <BenefitsPage /> },
      { path: 'training', element: <LearningPage /> },
      { path: 'messages', element: <MessagesPage /> },
      { path: 'notifications', element: <NotificationsPage /> },
      { path: 'new-starter', element: <NewStarterPage /> },

      // --- shared reference -------------------------------------------------
      { path: 'handbook', element: <HandbookPage /> },
      { path: 'policies', element: <PoliciesPage /> },
      { path: 'directory', element: <DirectoryPage /> },
      { path: 'calendar', element: <CalendarPage /> },

      // --- management -------------------------------------------------------
      { path: 'people', element: <EmployeeListPage /> },
      { path: 'people/:id', element: <EmployeeDetailPage /> },
      {
        path: 'analytics',
        element: (
          <RequireAuth capabilities={['employee:read:reports']}>
            <AnalyticsPage />
          </RequireAuth>
        ),
      },
      {
        path: 'approvals',
        element: (
          <RequireAuth capabilities={['employee:read:reports']}>
            <ApprovalsPage />
          </RequireAuth>
        ),
      },

      // --- administration ---------------------------------------------------
      // Guarded here as well as on every route on the server. This one is only
      // to avoid showing a screen that would fail.
      {
        path: 'admin/people',
        element: (
          <RequireAuth capabilities={['employee:create']}>
            <EmployeeAdminPage />
          </RequireAuth>
        ),
      },
      {
        path: 'admin/organisation',
        element: (
          <RequireAuth capabilities={['employee:write:all']}>
            <OrganisationPage />
          </RequireAuth>
        ),
      },
      {
        path: 'admin/payroll',
        element: (
          <RequireAuth capabilities={['employee:write:all']}>
            <PayrollAdminPage />
          </RequireAuth>
        ),
      },
      {
        path: 'admin/documents',
        element: (
          <RequireAuth capabilities={['employee:write:all']}>
            <ControlledDocumentsPage />
          </RequireAuth>
        ),
      },
      {
        path: 'admin/policies',
        element: (
          <RequireAuth capabilities={['employee:write:all']}>
            <PolicyAdminPage />
          </RequireAuth>
        ),
      },
      {
        path: 'admin/contracts',
        element: (
          <RequireAuth capabilities={['employee:write:all']}>
            <ContractAdminPage />
          </RequireAuth>
        ),
      },
      {
        path: 'admin/benefits',
        element: (
          <RequireAuth capabilities={['employee:write:all']}>
            <BenefitAdminPage />
          </RequireAuth>
        ),
      },
      {
        path: 'admin/training',
        element: (
          <RequireAuth capabilities={['employee:write:all']}>
            <CourseAdminPage />
          </RequireAuth>
        ),
      },
      { path: 'help', element: <HelpPage /> },
      {
        path: 'admin/accounts',
        element: (
          <RequireAuth capabilities={['user:manage']}>
            <UsersAdminPage />
          </RequireAuth>
        ),
      },
      {
        path: 'admin/settings',
        element: (
          <RequireAuth capabilities={['user:manage']}>
            <SettingsPage />
          </RequireAuth>
        ),
      },
      {
        path: 'admin/data',
        element: (
          <RequireAuth capabilities={['employee:write:all']}>
            <DataPage />
          </RequireAuth>
        ),
      },

      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
];
